import React, {
    useEffect,
    useMemo,
    useState,
    useCallback,
} from 'react';
import axios from 'axios';

import {
    Autosuggest,
    Button,
    Container,
    DateRangePicker,
    DateRangePickerProps,
    Flashbar,
    FlashbarProps,
    FormField,
    Header,
    Link,
    Select,
    SelectProps,
    SpaceBetween,
    Table,
    Box,
    Pagination,
    ToggleButton,
} from '@amzn/awsui-components-react';
import { useCollection } from '@amzn/awsui-collection-hooks';

import { useContinuousAuth } from '../../../pages/App';
import {
    datePickerI18nStrings,
    isValidRange,
    relativeDateOptions,
} from '../../../common/ComponentUtils';
import { useEmployeeData, UserRole } from '../../../common/EmployeeContext';
import { UserPermissions } from '../../../common/UserPermissions';
import settings from '../../../settings.json';

import ByTheNumbers from './ByTheNumbers';
import ViewDetailsModal from './ViewDetails';
import Styled from './styled';

import {
    CoachingDashboardProps,
    CoachingRecord,
    CoachingFetchResponse,
    CoachingStatus,
} from '../types';
import coachingCatalog from '../coaching-catalog';
import { coachingActions, hrActions } from '../form/Form';

const settingEnv = (settings as any)[window.location.host] || settings.default;
const { api_prefix: apiPrefix, cstools: { enableCsqaIntegration } } = settingEnv;

interface FetchCoachingParams {
    startDate?: string;
    endDate?: string;
    agentFilter?: string;
    supervisorFilter?: string;
    coachedByFilter?: string;
    statusFilter?: string;
    categoryFilter?: string;
    viewAll?: boolean;
}

const CoachingDashboard: React.FC<CoachingDashboardProps> = (props) => {
    const {
        user,
        userPermissions,
        role,
    } = props;
    const {
        agents,
        leads,
        supervisors,
        oms,
        loading: employeesLoading,
        error: employeesError
    } = useEmployeeData();

    const [loading, setLoading] = useState<boolean>(false);
    const [flashbarItems, setFlashbarItems] = useState<FlashbarProps.MessageDefinition[]>([]);

    // Data
    const [exportCoachings, setExportCoachings] = useState<CoachingRecord[]>([]);
    const [maxCount, setMaxCount] = useState(false);

    // Search
    const [value, setValue] = useState<DateRangePickerProps.Value | null>(null);
    const [startDate, setStartDate] = useState<string>('');
    const [endDate, setEndDate] = useState<string>('');
    const [agent, setAgent] = useState<string>('');
    const [supervisor, setSupervisor] = useState<string>('');
    const [coachedBy, setCoachedBy] = useState<string>('');
    const [searchCollapsed, setSearchCollapsed] = useState(false);

    // Filters
    const [selectedCategory, setSelectedCategory] = useState<string>('');
    const [selectedReason, setSelectedReason] = useState<string>('');
    const [selectedAction, setSelectedAction] = useState<string>('');
    const [selectedStatus, setSelectedStatus] = useState<string>('');

    // Separate 'displayedCoachings' that applies local filtering
    const [displayedCoachings, setDisplayedCoachings] = useState<CoachingRecord[]>([]);

    // Viewing & Editing (Single coaching item)
    const [selectedCoaching, setSelectedCoaching] = useState<CoachingRecord | null>(null);
    const [detailsModalOpen, setDetailsModalOpen] = useState<boolean>(false);
    const [detailLoading, setDetailLoading] = useState<boolean>(false);

    const { getHeaders } = useContinuousAuth();

    // Flashbar (Alert Banner) helpers
    const addFlashbarItem = useCallback((type: FlashbarProps.Type, content: React.ReactNode, id: string) => {
        setFlashbarItems((prev) => [
            ...prev,
            {
                type,
                content,
                dismissible: true,
                onDismiss: () => setFlashbarItems((items) => items.filter(i => i.id !== id)),
                id,
            }
        ]);
    }, []);

    const addErrorMessage = useCallback((messageText: string) => {
        addFlashbarItem('error', messageText, `error-${Date.now()}`);
    }, [addFlashbarItem]);

    const addSuccessMessage = useCallback((messageText: string) => {
        addFlashbarItem('success', messageText, `success-${Date.now()}`);
    }, [addFlashbarItem]);

    // Data Fetchers
    const fetchCoaching = useCallback(async (params: FetchCoachingParams) => {
        const {
            startDate,
            endDate,
            agentFilter,
            supervisorFilter,
            coachedByFilter,
            statusFilter,
            categoryFilter,
            viewAll,
        } = params;
        setLoading(true);
        setFlashbarItems([]);
        try {
            const queryParams: FetchCoachingParams = {
                ...(startDate && endDate && { startDate, endDate }),
                ...(agentFilter && { agentFilter }),
                ...(supervisorFilter && { supervisorFilter }),
                ...(coachedByFilter && { coachedByFilter }),
                ...(statusFilter && { statusFilter }),
                ...(categoryFilter && { categoryFilter }),
                ...(viewAll && { viewAll }),
            };

            const headers = await getHeaders();
            const { data } = await axios.get<CoachingFetchResponse>(
                `${apiPrefix}/api/coaching-tool`,
                { headers, params: queryParams }
            );
            const payload = data?.payload ?? [];
            const maxResults = data?.maxResults ?? false;
            setExportCoachings(payload);
            setMaxCount(!!maxResults);
            if (maxResults) {
                setFlashbarItems([{
                    type: 'error',
                    content: (
                        <>Max Results Displayed, consider reducing Date Range</>
                    ),
                    dismissible: true,
                    onDismiss: () => setFlashbarItems([]),
                    id: 'max-results-error'
                }]);
            }
            return data;
        } catch (err: any) {
            console.error('fetchCoaching Error:', err.message);
            addErrorMessage('There was a problem with your Request');
            return { payload: [], maxResults: false };
        } finally {
            setLoading(false);
        }
    }, [getHeaders, addErrorMessage]);

    // Define default table filters based on permissions/role
    const defaultFilter = useMemo<FetchCoachingParams | null>(() => {
        const rules: Array<[() => boolean, FetchCoachingParams]> = [
            // Admin
            [() => userPermissions.has(UserPermissions.AdminViewCoaching), { viewAll: true }],
            // HR
            [() => userPermissions.has(UserPermissions.HrCoaching),
            { statusFilter: CoachingStatus.Pending }],
            // Lead
            [() => userPermissions.has(UserPermissions.LeadViewCoaching), { coachedByFilter: user }],
            // OM / SeniorOM 
            [() => role !== undefined && [UserRole.Om, UserRole.SeniorOm].includes(role),
            { viewAll: true }],
            // Supervisor
            [() => role === UserRole.Supervisor,
            { supervisorFilter: user }],
            // Agent / Csqa
            [() => role !== undefined && [UserRole.Agent, UserRole.Csqa].includes(role),
            { agentFilter: user }],
        ];

        const found = rules.find(([predicate]) => predicate());
        return found ? found[1] : null;
    }, [role, user, userPermissions]);

    // ensure we only re-run reviewFetch when defaultFilter is defined/changes
    const defaultFilterKey = useMemo(() => {
        return defaultFilter ? JSON.stringify(defaultFilter) : '';
    }, [defaultFilter]);

    const defaultCoachingFetch = useCallback(() => {
        if (!defaultFilter) return;
        fetchCoaching(defaultFilter);
    }, [defaultFilter, fetchCoaching]);

    const fetchCoachingDetails = useCallback(async (
        id: string,
        createdTimestamp: number,
    ): Promise<CoachingRecord | null> => {
        try {
            const headers = await getHeaders();
            const { data } = await axios.get(`${apiPrefix}/api/coaching-record`, {
                headers,
                params: { id, createdTimestamp },
            });
            return data;
        } catch (error) {
            console.error('Error fetching Coaching Details:', error);
            return null;
        }
    }, [getHeaders]);

    // Employee Details
    const associatesList = useMemo(() => {
        if (userPermissions.has(UserPermissions.HrViewCoaching)) {
            const combined = [...agents, ...leads];
            return combined.sort((a, b) => a.Username.localeCompare(b.Username));
        }
        return agents;
    }, [agents, leads, userPermissions]);

    const coachedByList = useMemo(() => {
        const combined = [...leads, ...supervisors, ...oms];
        return combined.sort((a, b) => a.Username.localeCompare(b.Username));
    }, [leads, supervisors, oms]);

    if (employeesError) {
        addErrorMessage('There was a problem with your Request while retrieving hierarchy');
    }

    // On load: fetch reviews
    useEffect(() => {
        if (!defaultFilter) {
            // No matching rule (Display No Data)
            addErrorMessage('You do not have access to coaching records.');
            setExportCoachings([]);
            setMaxCount(false);
            setLoading(false);
            return;
        }

        // Set default search filter (if applicable)
        setAgent(defaultFilter.agentFilter || '');
        setSupervisor(defaultFilter.supervisorFilter || '');
        setCoachedBy(defaultFilter.coachedByFilter || '');

        (async () => {
            await fetchCoaching(defaultFilter);
        })();
    }, [
        defaultFilterKey,
        fetchCoaching,
    ]);

    // Table actions (View, Search, Filter)
    const handleViewDetails = useCallback(async (record: CoachingRecord) => {
        if (!record.id || !record.createdTimestamp) {
            console.error('Review ID or createdTimestmap is missing');
            addErrorMessage('Cannot load review details due to missing information.');
            return;
        }

        // Reset any stale data
        setSelectedCoaching(null);
        setDetailsModalOpen(true);
        setDetailLoading(true);

        try {
            const coachingRecord = await fetchCoachingDetails(record.id, record.createdTimestamp);
            if (coachingRecord) {
                setSelectedCoaching(coachingRecord);
            } else {
                addErrorMessage('Unable to load coaching details. Please try again.');
            }
        } catch (err) {
            console.error('Error fetching coaching details:', err);
            addErrorMessage('An error occurred while fetching coaching details.');
        } finally {
            setDetailLoading(false);
        }
    }, [addErrorMessage, getHeaders, fetchCoachingDetails]);

    const handleSearch = useCallback(() => {
        const isDateOnly = Boolean(
            startDate &&
            endDate &&
            !agent &&
            !supervisor &&
            !coachedBy
        );

        // if they only set dates, we include defaultFilter
        const searchParams: FetchCoachingParams = {
            ...(startDate && endDate && { startDate, endDate }),
            ...(agent && { agentFilter: agent }),
            ...(supervisor && { supervisorFilter: supervisor }),
            ...(coachedBy && { coachedByFilter: coachedBy }),
            ...(isDateOnly && defaultFilter ? defaultFilter : {}),
        };

        fetchCoaching(searchParams);
    }, [
        fetchCoaching,
        startDate,
        endDate,
        agent,
        supervisor,
        coachedBy,
        defaultFilter,
    ]);

    const handleReset = useCallback(() => {
        setAgent('');
        setSupervisor('');
        setCoachedBy('');
        setStartDate('');
        setEndDate('');
        setValue(null);

        defaultCoachingFetch();
    }, [defaultCoachingFetch]);


    const categoryOptions = useMemo(() => {
        const uniqueCategories = coachingCatalog.map(cat => cat.Category);
        return [{ label: 'All', value: '' }, ...uniqueCategories.map(c => ({ label: c, value: c }))];
    }, []);

    const reasonOptions = useMemo(() => {
        if (!selectedCategory) {
            return [{ label: 'All', value: '' }];
        }
        const foundCategory = coachingCatalog.find(cat => cat.Category === selectedCategory);
        if (!foundCategory) {
            return [{ label: 'All', value: '' }];
        }
        const reasons = foundCategory.reasons.map(r => ({ label: r.value, value: r.value }));
        return [{ label: 'All', value: '' }, ...reasons];
    }, [selectedCategory]);

    const actionOptions = useMemo(() => {
        return [
            { label: 'All', value: '' },
            ...coachingActions,
            ...hrActions,
        ];
    }, [coachingActions, hrActions]);

    // Derive unique statuses from the data
    const uniqueStatuses = useMemo(() => {
        const setStatuses = new Set(exportCoachings.map(c => c.status ?? '').filter(Boolean));
        return Array.from(setStatuses).sort();
    }, [exportCoachings]);
    const statusOptions = useMemo(() => {
        return [{ label: 'All', value: '' }, ...uniqueStatuses.map(s => ({ label: s, value: s }))];
    }, [uniqueStatuses]);

    // Each time user picks a filter or we load new data, apply them
    useEffect(() => {
        let filtered = [...exportCoachings];
        // Category
        if (selectedCategory) {
            filtered = filtered.filter(c => (c.category || '') === selectedCategory);
        }
        // Reason
        if (selectedReason) {
            filtered = filtered.filter(c => (c.reason || '') === selectedReason);
        }
        // Action
        if (selectedAction) {
            filtered = filtered.filter(c => (c.action || '') === selectedAction);
        }
        // Status
        if (selectedStatus) {
            filtered = filtered.filter(c => (c.status || '') === selectedStatus);
        }

        setDisplayedCoachings(filtered);
    }, [
        exportCoachings,
        selectedCategory,
        selectedReason,
        selectedAction,
        selectedStatus
    ]);

    // Filter Options
    const agentListOptions: SelectProps.Option[] = useMemo(() => {
        return associatesList.map(a => ({
            label: a.Username,
            value: a.Username
        }));
    }, [associatesList]);

    const supervisorListOptions: SelectProps.Option[] = useMemo(() => {
        return supervisors.map(s => ({
            label: s.Username,
            value: s.Username
        }));
    }, [supervisors]);

    const coachedByListOptions: SelectProps.Option[] = useMemo(() => {
        return coachedByList.map(c => ({
            label: c.Username,
            value: c.Username
        }));
    }, [coachedByList]);

    // Table columns & useCollection
    const columns = useMemo(() => {
        return [
            {
                id: 'actions',
                header: 'Actions',
                cell: (item: CoachingRecord) => (
                    <SpaceBetween direction='vertical' size='xs'>
                        <Link variant='primary' onFollow={() => handleViewDetails(item)}>
                            View Details
                        </Link>
                    </SpaceBetween>
                ),
                width: 130,
                minWidth: 130,
            },
            {
                id: 'date',
                header: 'Date',
                cell: (item: CoachingRecord) => item.date || '',
                width: 120,
                minWidth: 120,
            },
            {
                id: 'agent',
                header: 'Agent',
                cell: (item: CoachingRecord) => item.agent || '',
                minWidth: 180,
            },
            {
                id: 'supervisor',
                header: 'Supervisor',
                cell: (item: CoachingRecord) => item.supervisor || '',
                minWidth: 180,
            },
            {
                id: 'category',
                header: 'Category',
                cell: (item: CoachingRecord) => item.category || '',
                width: 150,
                minWidth: 150,
            },
            {
                id: 'reason',
                header: 'Reason',
                cell: (item: CoachingRecord) => item.reason || '',
                minWidth: 150,
            },
            {
                id: 'action',
                header: 'Action',
                cell: (item: CoachingRecord) => item.action || '',
                minWidth: 150,
            },
            {
                id: 'status',
                header: 'Status',
                cell: (item: CoachingRecord) => {
                    switch (item.status) {
                        case CoachingStatus.Rework:
                            return <Box color='text-status-error'>Rework</Box>;
                        case CoachingStatus.Pending:
                            return <Box color='text-status-info'>Pending</Box>;
                        case CoachingStatus.ReadyForDelivery:
                            return <Box color='text-status-info'>Ready</Box>;
                        case CoachingStatus.Delivered:
                            return <Box color='text-status-success'>Delivered</Box>;
                        case CoachingStatus.Deleted:
                            return <Box color='text-body-secondary'>Deleted</Box>;
                        default:
                            return item.status;
                    }
                },
                width: 150,
                minWidth: 150
            },
            {
                id: 'dueDate',
                header: 'Due Date',
                cell: (item: CoachingRecord) => item.status === 'Delivered' ? 'N/A' : (item.dueDate || 'N/A'),
                width: 120,
                minWidth: 120
            }
        ];
    }, [handleViewDetails, userPermissions]);

    const collectionOptions = useMemo(() => ({
        pagination: {
            pageSize: searchCollapsed ? 20 : 10,
        },
        selection: {},
        filtering: {},
    }), [searchCollapsed]);

    const { items, paginationProps } = useCollection(displayedCoachings, collectionOptions);

    return (
        <Styled.DashboardWrapper>
            {/* Flash Messages */}
            {flashbarItems.length > 0 && (
                <Flashbar items={flashbarItems} />
            )}

            {/* Filter Container */}
            <Styled.DashboardBody>
                {!searchCollapsed &&
                    <Styled.HeaderWrapper>
                        <Styled.SearchWrapper>
                            <Container header={<Header variant='h2'>Search</Header>} className='filter-container'>
                                <SpaceBetween size='m' direction='horizontal'>
                                    {/* DateRangePicker */}
                                    <FormField label='Date Range'>
                                        <DateRangePicker
                                            onChange={({ detail }) => {
                                                setValue(detail.value);
                                                if (detail.value && detail.value.type === 'absolute') {
                                                    setStartDate(detail.value.startDate || '');
                                                    setEndDate(detail.value.endDate || '');
                                                } else {
                                                    setStartDate('');
                                                    setEndDate('');
                                                }
                                            }}
                                            value={value}
                                            relativeOptions={relativeDateOptions}
                                            isValidRange={isValidRange}
                                            i18nStrings={datePickerI18nStrings}
                                            dateOnly
                                            placeholder='Search by date range'
                                            rangeSelectorMode='absolute-only'
                                        />
                                        {maxCount && (
                                            <Box color='text-status-error'>
                                                Max Results Displayed, please consider reducing your Date Range
                                            </Box>
                                        )}
                                    </FormField>

                                    {userPermissions.has(UserPermissions.SearchCoaching) && (
                                        <>
                                            {/* Agent */}
                                            <FormField label='Agent'>
                                                <Autosuggest
                                                    value={agent}
                                                    onChange={(e) => setAgent(e.detail.value)}
                                                    onSelect={(e) => setAgent(e.detail.value)}
                                                    options={agentListOptions}
                                                    placeholder='Search by Agent'
                                                    filteringType='auto'
                                                />
                                            </FormField>
                                            {/* Supervisor (supervisors should not have access to this filter) */}
                                            {role !== UserRole.Supervisor &&
                                                <FormField label='Supervisor'>
                                                    <Select
                                                        selectedOption={supervisor ? { label: supervisor, value: supervisor } : null}
                                                        onChange={({ detail }) => {
                                                            setSupervisor(detail.selectedOption.value || '');
                                                            setAgent('');
                                                            setCoachedBy('');
                                                        }}
                                                        options={supervisorListOptions}
                                                        placeholder='Search by Supervisor'
                                                    />
                                                </FormField>
                                            }
                                        </>
                                    )}

                                    {userPermissions.has(UserPermissions.HrViewCoaching) && role !== UserRole.Supervisor && (
                                        <FormField label='Coached By'>
                                            <Select
                                                selectedOption={coachedBy ? { label: coachedBy, value: coachedBy } : null}
                                                onChange={({ detail }) => {
                                                    setCoachedBy(detail.selectedOption.value || '');
                                                    setAgent('');
                                                    setSupervisor('');
                                                }}
                                                options={coachedByListOptions}
                                                placeholder='Search by Coached By'
                                            />
                                        </FormField>
                                    )}
                                </SpaceBetween>
                                {/* Search & Reset Buttons */}
                                <Styled.SearchSubmitWrapper>
                                    <SpaceBetween direction='horizontal' size='s' alignItems='center'>
                                        <Button variant='link' onClick={handleReset}>Reset</Button>
                                        <Button variant='primary' onClick={handleSearch}>Search</Button>
                                    </SpaceBetween>
                                </Styled.SearchSubmitWrapper>
                            </Container>
                        </Styled.SearchWrapper>
                        <Styled.FilterWrapper>
                            <Container
                                className='filter-container'
                                header={<Header variant='h2'>Filter</Header>}
                            >
                                <SpaceBetween size='m' direction='horizontal'>
                                    <FormField label='Category'>
                                        <Select
                                            selectedOption={selectedCategory ? { label: selectedCategory, value: selectedCategory } : null}
                                            onChange={({ detail }) => {
                                                setSelectedCategory(detail.selectedOption.value || '');
                                                setSelectedReason('');
                                                setSelectedAction('');
                                            }}
                                            options={categoryOptions}
                                            placeholder='Filter by Category'
                                        />
                                    </FormField>
                                    {
                                        selectedCategory &&
                                        <FormField label='Reason'>
                                            <Select
                                                selectedOption={selectedReason ? { label: selectedReason, value: selectedReason } : null}
                                                onChange={({ detail }) => {
                                                    setSelectedReason(detail.selectedOption.value || '');
                                                    setSelectedAction('');
                                                }}
                                                options={reasonOptions}
                                                placeholder='Filter by Reason'
                                            />
                                        </FormField>
                                    }
                                    <FormField label='Action'>
                                        <Select
                                            selectedOption={selectedAction ? { label: selectedAction, value: selectedAction } : null}
                                            onChange={({ detail }) => {
                                                setSelectedAction(detail.selectedOption.value || '');
                                            }}
                                            options={actionOptions as SelectProps.Option[]}
                                            placeholder='Filter by Action'
                                        />
                                    </FormField>
                                    <FormField label='Status'>
                                        <Select
                                            selectedOption={selectedStatus ? { label: selectedStatus, value: selectedStatus } : null}
                                            onChange={({ detail }) => {
                                                setSelectedStatus(detail.selectedOption.value || '');
                                            }}
                                            options={statusOptions}
                                            placeholder='Filter by Status'
                                        />
                                    </FormField>
                                </SpaceBetween>
                                <Styled.FilterSubmitWrapper>
                                    <Button variant='link' onClick={() => {
                                        setSelectedCategory('');
                                        setSelectedReason('');
                                        setSelectedAction('');
                                        setSelectedStatus('');
                                    }}>
                                        Clear
                                    </Button>
                                </Styled.FilterSubmitWrapper>
                            </Container>
                        </Styled.FilterWrapper>
                    </Styled.HeaderWrapper>
                }
                {/* Coaching Table */}
                <Styled.DashboardTable>
                    <Container header={<Styled.ContainerHeader>
                        <Styled.HeaderText>
                            <Header variant='h2'>Coaching Records</Header>
                        </Styled.HeaderText>
                        <Styled.HeaderButtons>
                            {displayedCoachings.length > 0 && (
                                <ByTheNumbers
                                    coachings={exportCoachings}
                                    userPermissions={userPermissions}
                                />
                            )}
                            <ToggleButton
                                ariaLabel={searchCollapsed ? 'Show Search Options' : 'Hide Search Options'}
                                onChange={({ detail }) => setSearchCollapsed(detail.pressed)}
                                pressed={searchCollapsed}
                                iconName='subtract-minus'
                                pressedIconName='add-plus'
                                wrapText={false}
                            >
                                {!searchCollapsed ? 'Hide Search Options' : 'Show Search Options'}
                            </ToggleButton>
                        </Styled.HeaderButtons>
                    </Styled.ContainerHeader>}>
                        <Table
                            trackBy='id'
                            loading={loading}
                            columnDefinitions={columns}
                            items={items}
                            variant='container'
                            resizableColumns
                            sortingDisabled
                            pagination={<Pagination {...paginationProps} />}
                        />
                    </Container>
                </Styled.DashboardTable>
            </Styled.DashboardBody>

            {/* View Details Modal */}
            {detailsModalOpen && (
                <ViewDetailsModal
                    loading={detailLoading}
                    visible={detailsModalOpen}
                    onDismiss={() => setDetailsModalOpen(false)}
                    selectedCoaching={selectedCoaching}
                    userPermissions={userPermissions}
                    enableCsqaIntegration={enableCsqaIntegration}
                    refreshData={defaultCoachingFetch}
                    addErrorMessage={addErrorMessage}
                    addSuccessMessage={addSuccessMessage}
                />
            )}
        </Styled.DashboardWrapper>
    );
};

export default CoachingDashboard;